/**
 * Fonte ÚNICA das opções de menu do sistema (Plano D, at-32).
 *
 * Consumida pela Home (`views/home/pages/home.tsx`) e pela navegação global
 * (`components/nav-global/NavGlobal.tsx`). Antes a Home tinha 7 blocos de JSX
 * copiados; duplicar essa lista no drawer garantiria divergência com data
 * marcada (mesma razão que levou as legendas de vistoria para `utils/` na at-24).
 *
 * A opção é DADO, não comportamento: guarda a rota e as flags, e quem navega
 * (Home ou drawer) aplica os efeitos colaterais — daí `limparCotacao` em vez de
 * um callback pronto, já que `dispatch`/`navigate` só existem dentro de componente.
 */
import { COTACAO } from '../cotacao/routes';
import { ASSOCIADOEDIT, ASSOCIADOSUBSTITUIR } from '../associado/routes';
import { ADESOES } from '../adesoes/routes';
import { VISTORIA } from '../vistoria/routes';
import { CARTEIRA } from '../carteira/routes';
import { ACOMPANHAMENTO_OFICINA } from '../oficina/routes';
import { TI } from '../ti/routes';

import carIcon from '../../img/car.svg';
import userPenIcon from '../../img/user-pen.svg';
import userPlusIcon from '../../img/user-plus.svg';
import fileDolar from '../../img/file-dolar.svg';
import file from '../../img/file.svg';
import computerIcon from '../../img/computer.svg';
import sendTrackingIcon from '../../img/send-tracking.svg';
import acompanhamentoIcon from '../../img/envio-acompanhamento.svg';

export interface OpcaoMenu {
  id: string;
  titulo: string;
  subtitulo: string;
  icone: string;
  rota: string;
  /** Visível só para TI/ADMIN (o projeto não tem RolesGuard: checagem manual). */
  somenteElevado?: boolean;
  /** Fluxo novo de cotação começa limpo — despacha `clearCotacaoData()` antes de navegar. */
  limparCotacao?: boolean;
  /**
   * Fluxo novo de ADESÃO começa limpo (at-38) — chama `iniciarNovaAdesao()`
   * antes de navegar. Sem isso a próxima adesão herdava o que sobrou da
   * anterior (o submit faz merge raso) e continuava amarrada ao rascunho dela.
   */
  limparAdesao?: boolean;
}

export const MENU_OPCOES: OpcaoMenu[] = [
  {
    id: 'cotacoes',
    titulo: 'Cotações',
    subtitulo: 'Acesse e gerencie as cotações.',
    icone: fileDolar,
    rota: COTACAO(),
    limparCotacao: true,
  },
  {
    // at-54 (item 55): leva à LISTA de adesões, não direto ao formulário. A adesão
    // nova começa no botão "Nova Adesão +" de lá — que é quem faz a limpeza do fluxo
    // (`iniciarNovaAdesao`), por isso `limparAdesao` saiu daqui.
    //
    // Antes, clicar em "Adesões" abria o cadastro e a lista `/adesoes` não estava no
    // menu: só se chegava a ela por caminhos laterais (a faixa de rascunho de
    // `/associado`, a carteira).
    id: 'adesoes',
    titulo: 'Adesões',
    subtitulo: 'Suas adesões e as que estão em andamento',
    icone: userPlusIcon,
    rota: ADESOES(),
  },
  {
    id: 'substituicao-veicular',
    titulo: 'Substituição Veicular',
    subtitulo: 'Alterar veículo cadastrado por um novo',
    icone: carIcon,
    rota: ASSOCIADOEDIT(),
  },
  {
    id: 'substituicao-titular',
    titulo: 'Substituição Titular',
    subtitulo: 'Alterar veículo vinculado à um associado',
    icone: userPenIcon,
    rota: ASSOCIADOSUBSTITUIR(),
  },
  {
    id: 'vistoria',
    titulo: 'Vistoria',
    subtitulo: 'Realizar ou visualizar vistoria existente',
    icone: file,
    rota: VISTORIA(),
  },
  {
    // Carteira do consultor (at-32): visível para qualquer papel — a carteira é
    // do usuário logado, então TI/ADMIN abrem a própria (possivelmente vazia).
    id: 'carteira',
    titulo: 'Minha Carteira',
    subtitulo: 'Seus associados e veículos, com o andamento de cada etapa',
    icone: sendTrackingIcon,
    rota: CARTEIRA(),
  },
  {
    // Acompanhamento de Oficina (3ª entrega da at-53): tela própria, e não uma
    // seção da carteira — decisão do Victor de 08/09/2026 ("nada de oficina na
    // carteira"). Como a carteira, é do usuário logado: qualquer papel abre a
    // própria, possivelmente vazia.
    id: 'acompanhamento-oficina',
    titulo: 'Acompanhamento de Oficina',
    subtitulo: 'Seus veículos em atendimento na oficina, com a etapa de cada um',
    icone: acompanhamentoIcon,
    rota: ACOMPANHAMENTO_OFICINA(),
  },
  {
    id: 'ti',
    titulo: 'Administração (TI)',
    subtitulo: 'Acesso exclusivo do time de TI',
    icone: computerIcon,
    rota: TI(),
    somenteElevado: true,
  },
];

/** Opções que o papel enxerga. Padrão do projeto: `userType` TI ou ADMIN. */
export const opcoesVisiveis = (userType?: string | null): OpcaoMenu[] =>
  MENU_OPCOES.filter(
    (opcao) => !opcao.somenteElevado || userType === 'TI' || userType === 'ADMIN',
  );

/**
 * Qual opção a rota atual representa (destaque do item ativo no drawer).
 *
 * Casa por prefixo e **vence a rota mais específica**: `/associado/edit` casa
 * tanto "Adesões" (`/associado`) quanto "Substituição Veicular"; sem o desempate
 * por comprimento os dois acenderiam ao mesmo tempo.
 */
export const idOpcaoAtiva = (opcoes: OpcaoMenu[], pathname: string): string | null => {
  let melhor: OpcaoMenu | null = null;

  opcoes.forEach((opcao) => {
    const casa = pathname === opcao.rota || pathname.startsWith(`${opcao.rota}/`);
    if (casa && (!melhor || opcao.rota.length > melhor.rota.length)) {
      melhor = opcao;
    }
  });

  return melhor ? (melhor as OpcaoMenu).id : null;
};
